import {
    ContouringCustomizationFile,
    CustomizationItem,
    UserCustomization,
} from '~/backend/types';
import { mvisionRoiCode } from '~/domain/comparison/mvisionRoiCodes';
import type { IdentifiedRoi } from '~/domain/comparison/roiCounterpart';

const DEFAULT_CUSTOMIZATION_NAME = 'default';

function findModelByName(
    models: UserCustomization<ContouringCustomizationFile>[],
    modelName?: string
): UserCustomization<ContouringCustomizationFile> | undefined {
    return models.find((model) => model.model_name === modelName);
}

function findCustomizationByName(
    model: UserCustomization<ContouringCustomizationFile>,
    customizationName: string
): CustomizationItem<ContouringCustomizationFile> | undefined {
    return model.customizations.find(
        (customization) =>
            customization.customization_name === customizationName
    );
}

function extractRoisFromCustomization(
    customization: CustomizationItem<ContouringCustomizationFile>
): (IdentifiedRoi & { included: boolean })[] {
    return customization.files.flatMap((file) =>
        file.rois.map((roi) => ({
            name: roi.name,
            included: roi.included,
            // The backend's code when it sends one, else MVision's code for the structure the model produces.
            code: roi.fma_id?.code_value
                ? {
                      value: roi.fma_id.code_value,
                      scheme: roi.fma_id.coding_scheme_designator,
                      meaning: roi.fma_id.code_meaning,
                  }
                : (mvisionRoiCode(roi.operation ?? '') ??
                  mvisionRoiCode(roi.name)),
        }))
    );
}

/**
 * Returns an array of unique ROI names from the target model and its customizations.
 *
 * @param models - An array of UserCustomization objects
 * @param modelName - The name of the target model
 * @param customizationName - The name of the customization (optional)
 * @returns An array of unique ROI names
 */
export function transformModelRoiNames(
    models: UserCustomization<ContouringCustomizationFile>[],
    modelName?: string,
    customizationName?: string
): {
    roiNames: string[];
    includedRoiNames: string[];
    rois: IdentifiedRoi[];
} {
    const targetModel = findModelByName(models, modelName);
    if (!targetModel) {
        return { roiNames: [], includedRoiNames: [], rois: [] };
    }

    const targetCustomization = customizationName
        ? findCustomizationByName(targetModel, customizationName)
        : findCustomizationByName(targetModel, DEFAULT_CUSTOMIZATION_NAME);

    if (!targetCustomization) {
        return { roiNames: [], includedRoiNames: [], rois: [] };
    }

    const rois = extractRoisFromCustomization(targetCustomization);
    const roiNames = rois.map((roi) => roi.name);
    const includedRoiNames = rois
        .filter((roi) => roi.included)
        .map((roi) => roi.name);

    return {
        roiNames,
        includedRoiNames,
        rois: rois.map(({ name, code }) => ({ name, code })),
    };
}
